import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TerminalBlock } from "@/components/ui/terminal-block";

/** Where the iamu CLI's install instructions live (README "Install"). */
export const CLI_INSTALL_URL = "https://github.com/iamu-dev/iamu#install";

interface Step {
  title: string;
  command: string;
  help: React.ReactNode;
}

/**
 * The commands a newcomer runs to put their first project on this
 * deployment. Verified against iamu-cli (`apps/cli/src/bin.ts`,
 * `commands/auth.ts`): `auth login` must run inside an initialised project,
 * creates a cloud project (or links one the user picks) and pushes the local
 * files; `sync` pushes later changes.
 */
export function onboardingCommands(apiUrl: string) {
  return {
    init: "iamu init",
    login: `iamu auth login --url ${apiUrl}`,
    sync: "iamu sync",
  };
}

/**
 * Empty state of `/projects`. The product runs in the user's editor through
 * the iamu CLI; this workspace is where synced projects are shared. So the
 * first route shown is connecting the CLI, and creating a project in the
 * browser is the secondary option.
 */
export function NoProjectsOnboarding({ apiUrl }: { apiUrl: string }) {
  const commands = onboardingCommands(apiUrl);
  const steps: Step[] = [
    {
      title: "Set up iamu in your repository",
      command: commands.init,
      help: (
        <>
          Run it in the project you want to test. It needs the iamu CLI on your
          PATH &mdash;{" "}
          <a
            href={CLI_INSTALL_URL}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-foreground underline underline-offset-4"
          >
            install instructions
          </a>
          .
        </>
      ),
    },
    {
      title: "Log in and link the project",
      command: commands.login,
      help: "Opens this site to sign you in, creates a project here (or links one you pick) and pushes your iamu files.",
    },
    {
      title: "Push later changes",
      command: commands.sync,
      help: "Run it after walkthroughs to share new findings with your team.",
    },
  ];

  return (
    <div className="max-w-2xl space-y-10">
      <section aria-labelledby="connect-cli-heading" className="space-y-6">
        <div className="space-y-2">
          <h2
            id="connect-cli-heading"
            className="text-lg font-semibold tracking-tight"
          >
            Connect your first project
          </h2>
          <p className="text-base text-muted-foreground">
            Iamu runs in your editor: the iamu CLI walks your product as
            personas and records findings in your repository. Link a project
            here to share scenarios, runs and findings with your team.
          </p>
        </div>

        <ol className="space-y-6">
          {steps.map((step, i) => (
            <li key={step.title} className="flex gap-4">
              <span
                aria-hidden="true"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-sm font-semibold tabular-nums"
              >
                {i + 1}
              </span>
              <div className="min-w-0 flex-1 space-y-2">
                <h3 className="text-base font-semibold">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.help}</p>
                <TerminalBlock
                  lines={[step.command]}
                  copyLabel={`Copy command: ${step.command}`}
                />
              </div>
            </li>
          ))}
        </ol>

        <p className="text-sm text-muted-foreground">
          Your project shows up here after the first push &mdash; this page
          checks for it automatically.
        </p>
      </section>

      <section
        aria-labelledby="other-ways-heading"
        className="space-y-4 border-t pt-6"
      >
        <h2 id="other-ways-heading" className="text-md font-semibold">
          Other ways in
        </h2>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            Starting in the browser instead? Create a project here and connect
            the CLI later.
          </p>
          <Button variant="outline" asChild className="shrink-0">
            <Link to="/projects/new">
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              New project
            </Link>
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          Invited by a teammate? Ask the project owner to add you &mdash; shared
          projects appear here once you are a member.
        </p>
      </section>
    </div>
  );
}
